import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Tooltip,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import { setProposalData } from "../../../../modules/proposal.slice";
import {
  useUploadCignaDocMutation,
  useVerifyCareCKYC,
} from "../../../../services/CKYC.service";
import { useUpdateProposalFormData } from "../../../../services/Proposal.service";
import {
  allowOnlyPanCard,
  allowOnlyNumbers,
  regex,
} from "../../../../utils/inputUtils";
import * as yup from "yup";
import Select from "../InputComponents/Select";
import TextInput from "../InputComponents/TextInput";
import { Check, Close } from "@mui/icons-material";
import { set } from "lodash";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";

const addressProofOptions = [
  { OPTION_KEY: "1", OPTION_VAL: "Passport" },
  { OPTION_KEY: "2", OPTION_VAL: "Voter Id" },
  { OPTION_KEY: "3", OPTION_VAL: "Driving License" },
  { OPTION_KEY: "7", OPTION_VAL: "Pan" },
];

const CKYCCareForm = () => {
  const { saveForm, isSaving } = useUpdateProposalFormData();
  const dispatch = useDispatch();
  const { activeProposal, proposalData } = useTypedSelector(
    (state) => state.proposal,
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { upload } = useUploadCignaDocMutation();
  const { verifyCareCKYC, isVerifyCareCKYC, referenceId } = useVerifyCareCKYC();
  const [otpTimer, setOtpTimer] = useState(0); // countdown state
  const [isUploading, setIsUploading] = useState(false);
  const handleVerifyPan = async (values) => {
    const requestData = {
      // dob: proposer_details?.dob,
      id_type: values.id_type,
      req_type: "otp",
      proposal_id: proposalData?.proposal_id,
    };

    if (values.id_type === "PAN") {
      requestData.no = values.no; // only add PAN
    }

    if (values.id_type === "AADHAAR") {
      requestData.no = values.no; // only add Aadhaar
    }

    const { data } = await verifyCareCKYC(requestData);

    dispatch(
      setProposalData({
        ...proposalData,
        ckyc_details: {
          ...proposalData?.ckyc_details,
          ...data?.return_data,
          status: data?.status,
        },
      }),
    );
  };

  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpTimer]);

  const handleUploadFiles = async (values) => {
    try {
      setIsUploading(true);

      const proposer_address_proof = values?.proposer_address_proof;
      const proposer_identity_proof = values?.proposer_identity_proof;

      const uploadResponse = await upload({
        proposer_address_proof,
        proposer_identity_proof,
      });

      if (!uploadResponse) {
        throw new Error("Upload failed. Please try again.");
      }

      const return_data = uploadResponse.return_data || {};

      dispatch(
        setProposalData({
          ...proposalData,
          ckyc_details: {
            ...proposalData?.ckyc_details,
            ...values,
            ...return_data,
          },
        }),
      );

      return return_data;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };
  const handleVerifyOtp = async (values) => {
    const { data } = await verifyCareCKYC({
      proposal_id: proposalData?.proposal_id,
      req_type: "verify",
      referenceId: referenceId,
      otp: values.otp,
    });
    dispatch(
      setProposalData({
        ...proposalData,
        ckyc_details: {
          ...proposalData?.ckyc_details,
          ...data?.return_data,
          status: data?.status,
        },
      }),
    );
  };
  const proposer_details = proposalData?.proposer_details || {};
  const ckyc_details = proposalData?.ckyc_details || {};

  const initialValues = {
    pan_no: proposer_details?.pan_no || "",
    address_document_type: ckyc_details?.address_document_type || "",
    identity_document_type: ckyc_details?.identity_document_type || "",
    proposer_address_proof: ckyc_details?.proposer_address_path || "",
    proposer_identity_proof: ckyc_details?.proposer_identity_path || "",
    proposer_address_proof_url: "",
    proposer_identity_proof_url: "",
    id_type: "PAN",
    no: "",
  };

  const handleResendOtp = async (values) => {
    await handleVerifyPan(values);
    setOtpTimer(40);
  };
  const startOtpTimer = () => setOtpTimer(40);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={yup.object().shape({
          pan_no: yup
            .string()
            .required("PAN Number is required")
            .matches(regex.pan_no, "Please Enter a valid PAN Number"),

          address_document_type: yup.string().when("status", {
            is: 500,
            then: (schema) => schema.required("Address Proof is required"),
          }),

          proposer_address_proof: yup.mixed().when("address_document_type", {
            is: (val) => val && val !== "",
            then: (schema) => schema.required("Please upload Address Proof"),
          }),

          identity_document_type: yup.string().when("status", {
            is: 500,
            then: (schema) => schema.required("Identity Proof is required"),
          }),

          proposer_identity_proof: yup.mixed().when("identity_document_type", {
            is: (val) => val && val !== "",
            then: (schema) => schema.required("Please upload Identity Proof"),
          }),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const uploadResponse = await handleUploadFiles(values);

            await saveForm({
              ckyc_details: {
                ...ckyc_details,
                ...values,
                ...uploadResponse,
              },
              from_screen: "ckyc",
            });
          } catch (error) {
            toast.error(error.message || "Upload failed");
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ setFieldValue, values, submitForm, isSubmitting }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <Select
                  name="id_type"
                  label="Select Document Type"
                  options={documentTypeOptions}
                  value={values.id_type || documentTypeOptions[0]?.OPTION_VAL}
                  onChange={(e) => setFieldValue("id_type", e.target.value)}
                  renderValue={(value) =>
                    value || documentTypeOptions[0]?.OPTION_VAL
                  }
                />
              </Grid>

              <Grid item xs={12} md={6} />

              {values.id_type === "PAN" && (
                <Grid item md={6} xs={12}>
                  <TextInput
                    label="Pan No."
                    name="no"
                    onInput={allowOnlyPanCard}
                  />
                </Grid>
              )}

              {values.id_type === "AADHAAR" && (
                <Grid item md={6} xs={12}>
                  <TextInput
                    label="Aadhaar No."
                    name="no"
                    onInput={allowOnlyNumbers}
                  />
                </Grid>
              )}
              <Grid item md={6} xs={12}>
                {ckyc_details?.verification_status ? (
                  <Chip
                    sx={{ mt: "36px" }}
                    variant="outlined"
                    label="Verified"
                    icon={<Check />}
                    color="success"
                  />
                ) : (
                  <Button
                    onClick={() => handleVerifyPan(values)}
                    disabled={isVerifyCareCKYC}
                    variant="contained"
                    sx={{
                      mt: "32px",
                      background: "#FF6024",
                      "&:hover": { background: "#f26d02" },
                    }}
                    type="button"
                  >
                    {isVerifyCareCKYC ? (
                      <CircularProgress size="30px" />
                    ) : (
                      "Verify PAN No."
                    )}
                  </Button>
                )}
              </Grid>
              {ckyc_details?.status === 200 && (
                <>
                  <Grid item md={6} xs={12}>
                    <TextInput
                      label="Verify Otp"
                      name="otp"
                      onInput={allowOnlyNumbers}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Box
                      sx={{
                        mt: "32px",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Button
                        onClick={async () => {
                          await handleVerifyOtp(values);
                          if (otpTimer === 0) startOtpTimer();
                        }}
                        // disabled={isVerifyCareCKYC}
                        variant="contained"
                        sx={{
                          background: "#FF6024",
                          "&:hover": { background: "#f26d02" },
                        }}
                        type="button"
                      >
                        Verify Otp
                      </Button>

                      {/* Countdown or Resend */}
                      {otpTimer > 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          Resend in {otpTimer}s
                        </Typography>
                      ) : (
                        <Button
                          onClick={() => handleResendOtp(values)}
                          variant="text"
                          size="small"
                          sx={{ color: "#FF6024" }}
                        >
                          Resend OTP
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </>
              )}
              {ckyc_details?.status === 500 && (
                <>
                  <Grid item md={6} xs={12}>
                    <Select
                      name="address_document_type"
                      options={addressProofOptions}
                      label="Address Proof"
                      value={values.address_document_type}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    {values?.proposer_address_proof ? (
                      <Box display="flex" flexDirection="column">
                        <Typography
                          fontSize="16px"
                          fontWeight="500"
                          marginBottom="8px"
                        >
                          View Address Document
                        </Typography>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          padding="1px"
                          border="1px solid #ccc"
                          borderRadius="4px"
                        >
                          <Box
                            flexGrow={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            padding="4px"
                          >
                            <a
                              href={
                                ckyc_details.proposer_address_path
                                  ? ckyc_details.proposer_address_path
                                  : values.proposer_address_proof_url
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {values.proposer_address_proof.name ||
                                "View Uploaded Address Proof"}
                            </a>
                          </Box>
                          <Tooltip title="Clear">
                            <IconButton
                              onClick={() => {
                                URL.revokeObjectURL(
                                  values.proposer_address_proof_url,
                                  "",
                                );
                                setFieldValue("proposer_address_proof", "");
                                setFieldValue("proposer_address_proof_url", "");
                              }}
                              aria-label="clear"
                            >
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ) : (
                      <TextInput
                        type="file"
                        name="proposer_address_proof"
                        label="Upload Address Proof Document"
                        onChange={(e) => {
                          const file = e.target.files[0];

                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error("File size cannot exceed 5 MB.");
                              return;
                            }

                            setFieldValue("proposer_address_proof", file);
                            setFieldValue(
                              "proposer_address_proof_url",
                              URL.createObjectURL(file),
                            );
                          }
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Select
                      name="identity_document_type"
                      options={addressProofOptions}
                      label="Identity Proof"
                      value={values.identity_document_type}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    {values.proposer_identity_proof ? (
                      <Box display="flex" flexDirection="column">
                        <Typography
                          fontSize="16px"
                          fontWeight="500"
                          marginBottom="8px"
                        >
                          View identity Document
                        </Typography>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          padding="1px"
                          border="1px solid #ccc"
                          borderRadius="4px"
                        >
                          <Box
                            flexGrow={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            padding="4px"
                          >
                            <a
                              href={
                                ckyc_details.proposer_identity_path ||
                                values.proposer_identity_proof_url
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {values.proposer_identity_proof.name ||
                                "View Uploaded identity Proof"}
                            </a>
                          </Box>
                          <Tooltip title="Clear">
                            <IconButton
                              onClick={() => {
                                URL.revokeObjectURL(
                                  values?.proposer_identity_proof_url,
                                  "",
                                );
                                setFieldValue(
                                  "proposer_identity_proof_url",
                                  "",
                                );
                                setFieldValue("proposer_identity_proof", "");
                              }}
                              aria-label="clear"
                            >
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ) : (
                      <TextInput
                        type="file"
                        name="proposer_identity_proof"
                        label="Upload Identity Proof Document"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error("File size cannot exceed 5 MB.");
                            return;
                          }
                          if (file) {
                            setFieldValue("proposer_identity_proof", file);
                            setFieldValue(
                              "proposer_identity_proof_url",
                              URL.createObjectURL(file),
                            );
                          }
                        }}
                      />
                    )}
                  </Grid>
                </>
              )}

              {(ckyc_details?.verification_status ||
                (values.proposer_identity_proof &&
                  values.proposer_address_proof)) && (
                <Grid
                  item
                  xs={12}
                  justifyContent="center"
                  display={"flex"}
                  my={1}
                >
                  <Box sx={{ position: "relative" }}>
                    <Button
                      size={isMobile ? "medium" : "large"}
                      variant="contained"
                      disabled={isSaving}
                      onClick={submitForm}
                      sx={{
                        background: "#FF6024",
                        "&:hover": { background: "#f26d02" },
                      }}
                    >
                      {isSaving || isUploading ? (
                        <CircularProgress size="30px" />
                      ) : (
                        "Proceed to Insurer Details"
                      )}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CKYCCareForm;

const documentTypeOptions = [
  { OPTION_KEY: "PAN", OPTION_VAL: "PAN" },
  { OPTION_KEY: "AADHAAR", OPTION_VAL: "AADHAAR" },
];
